import React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { HeroKpi } from '@/components/hero-kpi';
import { KpiStrip } from '@/components/kpi-strip';
import { Leaderboard } from '@/components/leaderboard';
import { BreakdownBars } from '@/components/breakdown-bars';
import { InsightsPanel } from '@/components/insights-panel';
import { ActivityChart } from '@/routes/console/_authenticated/_layout/-features/activity-chart';
import { InsightsHistory } from '@/routes/console/_authenticated/_layout/-features/insights-history';
import { zodValidator } from '@tanstack/zod-adapter';
import { dateRangeSchema } from '@/schema/date-range-schema';
import { endOfDay, format, startOfDay, subDays } from 'date-fns';
import { DATE_FORMAT_ISO } from '@/constants/date';
import { FiltersRow } from '@/components/filter-row';
import { AnalyticsFilter } from '@/components/analytics-filter';
import { useQuery } from '@tanstack/react-query';
import { fetchWorkspaceAnalytics } from '@/services/workspace';
import { useAuthenticationState } from '@/hooks/use-authentication';
import { Loading } from '@/components/loader/loading';
import { computeWorkspaceInsights, halfPeriodDelta } from '@/helpers/analytics-insights.helper';
import { paletteFor } from '@/helpers/breakdown-palette.helper';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
const dashboardSearchSchema = dateRangeSchema.extend({
    excludeDevices: z.array(z.string()).default(['bot']),
    excludeBrowsers: z.array(z.string()).default(['bot']),
});
export const Route = createFileRoute('/console/_authenticated/_layout/')({
    component: DashboardPage,
    validateSearch: zodValidator(dashboardSearchSchema),
});
function DashboardPage() {
    const navigate = Route.useNavigate();
    const search = Route.useSearch();
    const { workspace } = useAuthenticationState();
    const { t } = useTranslation('glossary');
    const [from, to] = React.useMemo(() => {
        const fromDate = new Date(search.from);
        const toDate = new Date(search.to);
        const validFrom = isNaN(fromDate.getTime()) ? startOfDay(subDays(new Date(), 6)) : fromDate;
        const validTo = isNaN(toDate.getTime()) ? endOfDay(new Date()) : toDate;
        return [validFrom, validTo];
    }, [search.from, search.to]);
    const setRange = React.useCallback((newFrom, newTo) => {
        navigate({
            search: {
                from: format(newFrom, DATE_FORMAT_ISO),
                to: format(newTo, DATE_FORMAT_ISO),
                excludeDevices: search.excludeDevices,
                excludeBrowsers: search.excludeBrowsers,
            },
        });
    }, [navigate, search.excludeDevices, search.excludeBrowsers]);
    const handleDevicesChange = React.useCallback((values) => {
        navigate({
            search: {
                from: search.from,
                to: search.to,
                excludeDevices: values,
                excludeBrowsers: search.excludeBrowsers,
            },
        });
    }, [navigate, search.from, search.to, search.excludeBrowsers]);
    const handleBrowsersChange = React.useCallback((values) => {
        navigate({
            search: {
                from: search.from,
                to: search.to,
                excludeDevices: search.excludeDevices,
                excludeBrowsers: values,
            },
        });
    }, [navigate, search.from, search.to, search.excludeDevices]);
    const { data: analytics, isPending } = useQuery({
        queryKey: [
            'workspace-analytics',
            workspace?.publicId,
            format(from, DATE_FORMAT_ISO),
            format(to, DATE_FORMAT_ISO),
            search.excludeDevices,
            search.excludeBrowsers,
        ],
        queryFn: () => fetchWorkspaceAnalytics({
            workspaceId: workspace.publicId,
            from,
            to,
            excludeDevices: search.excludeDevices,
            excludeBrowsers: search.excludeBrowsers,
        }),
        enabled: !!workspace?.publicId,
    });
    const periodLabel = React.useMemo(() => `${format(from, 'MMM d')} – ${format(to, 'MMM d, yyyy')}`, [from, to]);
    return (<div className="flex flex-col gap-6 p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="space-y-1">
                    <h1 className="text-2xl font-semibold tracking-tight">{t('dashboard.title')}</h1>
                    <p className="text-sm text-muted-foreground">{t('dashboard.subtitle')}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full border border-border/60 bg-muted/40 px-3 py-1 text-xs text-muted-foreground tabular-nums">
                        {periodLabel}
                    </span>
                    <Button asChild size="sm">
                        <Link to="/console/campaigns/new">
                            <Plus className="mr-1 h-4 w-4"/>
                            {t('dashboard.actions.new_campaign', { defaultValue: 'New campaign' })}
                        </Link>
                    </Button>
                </div>
            </div>

            <div className="sticky top-0 z-10 -mx-6 -my-1 rounded-xl border border-border/60 bg-card/70 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-card/50">
                <div className="flex flex-wrap items-end justify-between gap-3">
                    <FiltersRow from={from} to={to} setRange={setRange}/>
                    <AnalyticsFilter excludeDevices={search.excludeDevices} excludeBrowsers={search.excludeBrowsers} onDevicesChange={handleDevicesChange} onBrowsersChange={handleBrowsersChange}/>
                </div>
            </div>

            {isPending ? (<Loading loadingText={t('dashboard.loading_analytics')}/>) : (<DashboardContent analytics={analytics} workspaceId={workspace?.publicId} from={from} to={to} excludeDevices={search.excludeDevices} excludeBrowsers={search.excludeBrowsers}/>)}
        </div>);
}
const DEVICE_LABELS = {
    iphone: 'iPhone',
    android: 'Android',
    mobile: 'Mobile',
    ipad: 'iPad',
    tablet: 'Tablet',
    desktop: 'Desktop',
    macintosh: 'Macintosh',
    bot: 'Bot',
    unknown: 'Unknown',
    other: 'Other',
    tv: 'TV',
};
const BROWSER_LABELS = {
    chrome: 'Chrome',
    safari: 'Safari',
    firefox: 'Firefox',
    edge: 'Edge',
    opera: 'Opera',
    facebook: 'Facebook',
    instagram: 'Instagram',
    tiktok: 'TikTok',
    twitter: 'Twitter',
    linkedin: 'LinkedIn',
    reddit: 'Reddit',
    whatsapp: 'WhatsApp',
    telegram: 'Telegram',
    messenger: 'Messenger',
    pinterest: 'Pinterest',
    bot: 'Bot',
    unknown: 'Other',
};
function DashboardContent({ analytics, workspaceId, from, to, excludeDevices, excludeBrowsers, }) {
    const { t } = useTranslation('glossary');
    const kpi = analytics?.kpi;
    const entries = kpi?.entries ?? 0;
    const timeline = React.useMemo(() => analytics?.timeline ?? [], [analytics?.timeline]);
    const campaigns = React.useMemo(() => analytics?.campaignBreakdown ?? [], [analytics?.campaignBreakdown]);
    const intents = React.useMemo(() => analytics?.intentBreakdown ?? [], [analytics?.intentBreakdown]);
    const devices = React.useMemo(() => analytics?.devices ?? [], [analytics?.devices]);
    const browsers = React.useMemo(() => analytics?.browsers ?? [], [analytics?.browsers]);
    const intentRate = entries > 0 ? (((kpi?.intents ?? 0) / entries) * 100).toFixed(1) + '%' : '0.0%';
    const destinationCTR = entries > 0 ? (((kpi?.destinationClicks ?? 0) / entries) * 100).toFixed(1) + '%' : '0.0%';
    const scansDelta = halfPeriodDelta(timeline.map((p) => p.count));
    const sparkData = React.useMemo(() => timeline.map((p) => ({ date: p.date, value: p.count })), [timeline]);
    const campaignRows = React.useMemo(() => [...campaigns]
        .sort((a, b) => b.entries - a.entries)
        .map((c, i) => ({
        key: String(c.campaignId),
        label: c.campaignName,
        value: c.entries,
        barClassName: paletteFor(i),
    })), [campaigns]);
    const intentEntries = React.useMemo(() => intents.map((intent, i) => ({
        key: intent.intent,
        label: intent.intent,
        value: intent.count,
        barClassName: paletteFor(i),
    })), [intents]);
    const deviceEntries = React.useMemo(() => [...devices]
        .sort((a, b) => b.scans - a.scans)
        .map((d, i) => ({
        key: d.deviceType,
        label: DEVICE_LABELS[d.deviceType] ?? d.deviceType,
        value: d.scans,
        barClassName: paletteFor(i),
    })), [devices]);
    const browserEntries = React.useMemo(() => [...browsers]
        .sort((a, b) => b.scans - a.scans)
        .map((b, i) => ({
        key: b.browserType,
        label: BROWSER_LABELS[b.browserType] ?? b.browserType,
        value: b.scans,
        barClassName: paletteFor(i),
    })), [browsers]);
    const insights = React.useMemo(() => computeWorkspaceInsights({ kpi, timeline, campaigns, devices, browsers }), [kpi, timeline, campaigns, devices, browsers]);
    return (<>
            <div className="grid gap-4 lg:grid-cols-3">
                <HeroKpi className="lg:col-span-2" label={t('dashboard.metrics.total_scans')} value={entries.toLocaleString()} sublabel={entries > 0 ? t('scans_this_period', { defaultValue: 'scans this period' }) : undefined} delta={scansDelta} deltaLabel={scansDelta !== null
            ? t('vs_previous_period', { defaultValue: 'vs. previous period' })
            : undefined} spark={sparkData}/>
                <InsightsPanel insights={insights}/>
            </div>

            <KpiStrip items={[
            {
                label: t('dashboard.metrics.unique_visitors'),
                value: (kpi?.uniqueVisitors ?? 0).toLocaleString(),
                hint: kpi && kpi.entries > 0 && kpi.uniqueVisitors > 0
                    ? `${(kpi.entries / kpi.uniqueVisitors).toFixed(1)}× scans / visitor`
                    : undefined,
            },
            {
                label: t('dashboard.metrics.intent_rate'),
                value: intentRate,
                hint: `${(kpi?.intents ?? 0).toLocaleString()} intents`,
            },
            {
                label: t('dashboard.metrics.destination_ctr'),
                value: destinationCTR,
                hint: `${(kpi?.destinationClicks ?? 0).toLocaleString()} clicks`,
            },
            {
                label: t('dashboard.metrics.daily_avg', { defaultValue: 'Daily avg' }),
                value: timeline.length > 0 ? Math.round(entries / timeline.length).toLocaleString() : '0',
                hint: `${timeline.length} ${timeline.length === 1 ? 'day' : 'days'}`,
            },
        ]}/>

            <ActivityChart timeline={timeline}/>

            {workspaceId ? (<InsightsHistory workspaceId={workspaceId} from={from} to={to} excludeDevices={excludeDevices} excludeBrowsers={excludeBrowsers}/>) : null}

            <div className="grid gap-4 lg:grid-cols-2">
                <Leaderboard title={t('dashboard.leaderboard.campaigns', { defaultValue: 'Top campaigns' })} description={t('dashboard.leaderboard.campaigns_hint', {
            defaultValue: 'Ranked by scans in the selected window.',
        })} rows={campaignRows} valueLabel={t('scans', { ns: 'common', defaultValue: 'Scans' })}/>
                <BreakdownBars title={t('dashboard.intent_breakdown.title', { defaultValue: 'Intent breakdown' })} description={t('dashboard.intent_breakdown.hint', {
            defaultValue: 'What visitors did after scanning.',
        })} entries={intentEntries}/>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
                <BreakdownBars title={t('analytics.dt.title', { defaultValue: 'Device breakdown' })} description={t('analytics.dt.description', {
            defaultValue: 'Distribution of devices across scans.',
        })} entries={deviceEntries}/>
                <BreakdownBars title={t('analytics.bt.title', { defaultValue: 'Browser breakdown' })} description={t('analytics.bt.description', {
            defaultValue: 'Distribution of browsers across scans.',
        })} entries={browserEntries}/>
            </div>
        </>);
}
